const isBrowser = typeof window !== 'undefined';

/**
 * Validate a post-sign-in redirect target, returning a same-origin
 * pathname+search+hash or undefined.
 *
 * `linkBack` is attacker-controllable: it arrives in the URL of /sign-in, so
 * anyone can hand a user a sign-in link that sends them somewhere else after
 * they authenticate. Every consumer must pass it through here.
 *
 * Everything is resolved against the current origin before being judged, rather
 * than accepting anything that merely starts with '/'. That shape check let
 * through backslash protocol-relative inputs: browsers resolve '/\evil.example'
 * to https://evil.example/, exactly as they do '//evil.example', so a
 * startsWith('//') test alone is not enough. Parsing first means the origin
 * comparison decides, whatever the input looks like.
 */
export const normalizeAuthRedirectLink = (link: unknown): string | undefined => {
  if (typeof link !== 'string') return undefined;

  const trimmedLink = link.trim();
  if (!trimmedLink || !isBrowser) return undefined;

  try {
    const url = new URL(trimmedLink, window.location.origin);

    if (url.origin !== window.location.origin) return undefined;

    // Redirecting back to sign-in would loop. Compare with the trailing slash
    // stripped: the route is declared as '/sign-in/', so both spellings resolve
    // there and only checking one of them leaves the loop reachable.
    const pathname = url.pathname.replace(/\/+$/, '') || '/';
    if (pathname === '/sign-in') return undefined;

    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return undefined;
  }
};
